import {
  getFindPersonDetailsQueryKey,
  mapContactCardType,
  useRegenerateContactCard as useRegenerateContactCardMutation,
} from '@api';
import { useInvalidateQuery, useNotification } from '@common';
import { modals } from '@mantine/modals';
import { isAxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

const TRANS_KEY_MODAL = 'COMPANY_LINKS.CONTACT_CARD.REGENERATE_CONFIRM_MODAL';
const TRANS_KEY_NOTIFICATION = 'NOTIFICATIONS.REGENERATE_CONTACT_CARD';

type RegeneratedContactCardType = NonNullable<
  ReturnType<typeof mapContactCardType>
>;

type UseRegenerateContactCardArgsType = {
  personId: string;
  onSuccess: (contactCard: RegeneratedContactCardType) => void;
};

type UseRegenerateContactCardReturnType = {
  onRegenerate: (companyLinkId: string) => void;
};

export const useRegenerateContactCard = ({
  personId,
  onSuccess,
}: UseRegenerateContactCardArgsType): UseRegenerateContactCardReturnType => {
  const { t } = useTranslation('personDetailsGeneral');
  const { mutateAsync } = useRegenerateContactCardMutation();
  const { showError, showSuccess } = useNotification();
  const { invalidateQuery } = useInvalidateQuery(
    getFindPersonDetailsQueryKey(personId)
  );

  const regenerateContactCard = async (
    companyLinkId: string
  ): Promise<void> => {
    try {
      const response = await mutateAsync({ companyLinkId });
      const regenerated = mapContactCardType(response.data);
      if (!regenerated) {
        return;
      }

      await invalidateQuery();
      showSuccess({
        title: `${TRANS_KEY_NOTIFICATION}.SUCCESS.TITLE`,
        message: `${TRANS_KEY_NOTIFICATION}.SUCCESS.MESSAGE`,
      });
      onSuccess(regenerated);
    } catch (error) {
      if (isAxiosError(error)) {
        showError({
          title: `${TRANS_KEY_NOTIFICATION}.ERROR.TITLE`,
          message: `${TRANS_KEY_NOTIFICATION}.ERROR.MESSAGE`,
          error,
        });
      }
    }
  };

  const onRegenerate = (companyLinkId: string): void => {
    modals.openConfirmModal({
      title: t(`${TRANS_KEY_MODAL}.TITLE`),
      children: t(`${TRANS_KEY_MODAL}.MESSAGE`),
      labels: {
        confirm: t(`${TRANS_KEY_MODAL}.CONFIRM_BUTTON`),
        cancel: t('CANCEL_BUTTON', { ns: 'common' }),
      },
      confirmProps: {
        color: 'red',
      },
      onConfirm: () => {
        void regenerateContactCard(companyLinkId);
      },
    });
  };

  return { onRegenerate };
};
